import { compact } from 'lodash'
import { UserPosition } from 'app/generated/graphql'
import { toUserPosition } from 'app/lib/userPosition'

/**
 * Pure mappers from the parsed user-details form body to the GraphQL mutation
 * inputs. Extracted from `useUserDetails` so the create/update shaping can be
 * unit-tested without the hook's many dependencies (TRK-6070 restored this
 * submit path after the Remix-to-SPA migration dropped the route's action).
 *
 * The form parses hidden inputs through the validated-form shim, so `staffId`
 * arrives as an object (`{ id, name }`) or a string, and `allowedLocationIds`
 * as a single string or an array. These coerce both to the mutation shape.
 */

export type CreateUserInputShape = {
  name: string
  email: string
  phone?: string
  roleId?: string
  staffId?: string
  allowedLocationIds?: string[]
}

export type UpdateUserInputShape = {
  name?: string
  email?: string
  phone?: string
  roleId?: string
  allowedLocationIds?: string[]
  position?: UserPosition
  jobTitle?: string
}

export function toLocationIds(value: unknown): string[] | undefined {
  if (value == null || value === ``) return undefined
  const list = Array.isArray(value) ? value : [value]
  const ids = compact(list.map((item) => (typeof item === `string` ? item : undefined)))
  return ids.length ? ids : undefined
}

export function toStaffId(value: unknown): string | undefined {
  if (!value) return undefined
  if (typeof value === `string`) return value || undefined
  if (typeof value === `object` && `id` in (value as Record<string, unknown>)) {
    const id = (value as { id?: unknown }).id
    return typeof id === `string` && id ? id : undefined
  }
  return undefined
}

const text = (value: unknown): string | undefined =>
  typeof value === `string` && value.trim() ? value.trim() : undefined

export function buildCreateUserInput(
  body: Record<string, unknown>,
  fallbackRoleId: string | undefined,
): CreateUserInputShape | undefined {
  const name = text(body.name)
  const email = text(body.email)
  if (!name || !email) return undefined
  return {
    name,
    email,
    phone: text(body.phone),
    roleId: text(body.roleId) ?? (fallbackRoleId || undefined),
    staffId: toStaffId(body.staffId),
    allowedLocationIds: toLocationIds(body.allowedLocationIds),
  }
}

export function buildUpdateUserInput(
  body: Record<string, unknown>,
  fallbackRoleId: string | undefined,
): UpdateUserInputShape {
  // `position` arrives as a display label (e.g. `Treatment Coordinator`); the
  // mutation input is the `UserPosition` enum, so convert at the boundary and
  // omit it when the label is empty or unknown (TRK-6072). `jobTitle` is only
  // meaningful when position is `Other`, but the API accepts it either way, so
  // pass whatever the form submitted.
  return {
    name: text(body.name),
    email: text(body.email),
    phone: text(body.phone),
    roleId: text(body.roleId) ?? (fallbackRoleId || undefined),
    allowedLocationIds: toLocationIds(body.allowedLocationIds),
    position: toUserPosition(body.position),
    jobTitle: text(body.jobTitle),
  }
}
